import React, {ReactElement} from "react"
import {
	AllowedProductApplicationsColumns,
	getProductApplicationName,
	ProductApplication,
	ProductApplicationsColumns,
	ProductApplicationStatus,
} from "../../resources/productApplication"
import {Resource} from "../../resources/common"
import {useNavigateWithListPath} from "../../hooks/useListReturnPath"
import Icon from "../Icon/Icon"
import classNames from "classnames"
import {DataTableCell, DataTableRow} from "../DataTable/DataTable"
import {OrgName} from "../OrgName/OrgName"
import Tag, {TagTheme} from "../Tag/Tag"
import {startCase} from "lodash"
import moment from "moment"
import IdLink from "../IdLink/IdLink"

interface ProductApplicationRowProps {
	productApplication: ProductApplication
	include?: Resource[]
	includedColumns: Array<AllowedProductApplicationsColumns>
}

export default function ProductApplicationRow({
	productApplication,
	include,
	includedColumns,
}: ProductApplicationRowProps) {
	const navigateWithListPath = useNavigateWithListPath()

	const name = getProductApplicationName(productApplication)
	const id = productApplication.id
	const org = productApplication.relationships.org?.data.id
	const status = productApplication.attributes.status
	const createdAt = productApplication.attributes.createdAt
	const updatedAt = productApplication.attributes.updatedAt
	const customerId = productApplication.relationships.customer?.data.id

	let iconType: JSX.Element = <></>

	switch (productApplication.type) {
		case "businessProductApplication":
			iconType = <Icon icon={"toolbox"} />
			break
		case "individualProductApplication":
			iconType = <Icon icon={"user-geometric-action---users"} />
			break
	}

	const contentColumns: Partial<Record<AllowedProductApplicationsColumns, ReactElement>> = {}

	if (includedColumns.includes(ProductApplicationsColumns.id)) {
		contentColumns["Id"] = (
			<DataTableCell className={classNames("data-table-id-cell")}>
				<IdLink href={`/product-applications/${id}`}>{id}</IdLink>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(ProductApplicationsColumns.name)) {
		contentColumns["Name"] = <DataTableCell>{name}</DataTableCell>
	}

	if (includedColumns.includes(ProductApplicationsColumns.type)) {
		contentColumns["Type"] = <DataTableCell>{iconType}</DataTableCell>
	}

	if (includedColumns.includes(ProductApplicationsColumns.org)) {
		contentColumns["Org"] = <OrgName orgId={org.toString()} included={include} element={DataTableCell} />
	}

	if (includedColumns.includes(ProductApplicationsColumns.status)) {
		const statusRecord: Record<ProductApplicationStatus, TagTheme> = {
			[ProductApplicationStatus.AwaitingDocuments]: "neutral",
			[ProductApplicationStatus.PendingReview]: "warning",
			[ProductApplicationStatus.Approved]: "success",
			[ProductApplicationStatus.Denied]: "error",
			[ProductApplicationStatus.Pending]: "warning",
			[ProductApplicationStatus.Canceled]: "error",
			[ProductApplicationStatus.PendingSubmission]: "neutral",
			[ProductApplicationStatus.PreApproved]: "success",
		}

		contentColumns["Status"] = (
			<DataTableCell>
				<Tag size="large" variant="outline" theme={statusRecord[status]} content={startCase(status)} />
			</DataTableCell>
		)
	}

	if (includedColumns.includes(ProductApplicationsColumns.customerId)) {
		contentColumns["Customer ID"] = (
			<DataTableCell className={classNames("data-table-id-cell")}>
				{customerId ? <IdLink href={`/customers/${customerId}`}>{customerId}</IdLink> : "—"}
			</DataTableCell>
		)
	}

	if (includedColumns.includes(ProductApplicationsColumns.createdAt)) {
		contentColumns["Created At"] = <DataTableCell>{moment(createdAt).format("L LT")}</DataTableCell>
	}

	if (includedColumns.includes(ProductApplicationsColumns.updatedAt)) {
		contentColumns["Updated At"] = (
			<DataTableCell>
				{updatedAt ? moment(updatedAt).format("L LT") : <span className="empty-date-placeholder"> --/--/---- </span>}
			</DataTableCell>
		)
	}

	const content: Array<ReactElement | null> = []

	includedColumns.forEach((col, i) => {
		if (col in contentColumns && contentColumns[col]) {
			const column = {...contentColumns[col]} as ReactElement
			column.key = i.toString()
			content.push(column)
		}
	})

	return (
		<DataTableRow
			onClick={(e) => {
				e.preventDefault()
				navigateWithListPath(`/product-applications/${id}`)
			}}
		>
			{content}
		</DataTableRow>
	)
}
